import { Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { MetricsProvider } from '@studio/metrics/client/MetricsProvider';
import { LoadReadinessCheckerSLIObserver } from '@studio/metrics/components/LoadReadinessCheckerSLIObserver';
import { ATTRIBUTE_PERSONA_TYPE_SPEAKER } from '@studio/metrics/constants/PersonaType';
import { getEnvironmentType } from '@studio/constants/EnvironmentTypes';
import { broadcastTypesMap } from '@studio/constants/BroadcastTypes';
import { VENDOR_AGORA } from '@studio/constants/Vendor';
import { MEETING_STATUS } from '@studio/constants/Meeting';
import { shallowEqual, useSelector } from 'react-redux';
import React, { useMemo, useState, useEffect, lazy, Suspense } from 'react';
import _ from 'underscore';
import { nanoid } from 'nanoid';
import LostRealtimeSocketMessage from 'containers/modals/LostRealtimeSocketMessage/LostRealtimeSocketMessage';
import { AudioInputFailed } from '@shoflo/polymer/containers/modals/AudioInputFailed/AudioInputFailed';
import CreateMeetingError from '@shoflo/polymer/containers/modals/CreateMeetingError/CreateMeetingError';
import { MeetingEndedModal } from '@shoflo/polymer/containers/modals/MeetingEnded/MeetingEnded';
import { UnauthorizedError } from '@shoflo/polymer/containers/modals/UnauthorizedError/UnauthorizedError';
import AppContext from '@shoflo/polymer/contexts/AppContext';
import BrandContext from '@shoflo/polymer/contexts/BrandContext';
import useLaunchDarkly from '@shoflo/polymer/hooks/useLaunchDarkly/useLaunchDarkly';
import { getEnvVar } from '@shoflo/polymer/utils/getEnvVar';
import { useLDPayload } from '@shoflo/studio-redux/hooks/useLDPayload/useLDPayload';
import { useRequireAuth } from '@shoflo/studio-redux/hooks/useRequireAuth/useRequireAuth';

import { datadogRum } from '@datadog/browser-rum';

import Paths from './AppPaths';
import { LIGHT_THEME } from 'constants/Theme';
import { BROADCAST_TYPE } from 'constants/SearchParams';
import GlobalStyle from './GlobalStyle.styled';
import ThemeContext from 'contexts/ThemeContext';
import ScreenshareModal from 'containers/modals/Screenshare/Screenshare';
import GenericSettingsModal from 'containers/modals/Settings/GenericSettings';
import AwaitingKnockApprovalModal from 'containers/modals/AwaitingKnockApproval/AwaitingKnockApproval';
import InvalidJoinRequestModal from 'containers/modals/InvalidJoinRequestModal/InvalidJoinRequestModal';
import { datadogLogs } from '@datadog/browser-logs';
import { getURLPathParams } from '@studio/logger/utils/getURLPathParams';
import packageJSON from '../package.json';

import { Loading } from '@shoflo/polymer/components/Loader/Loader';
import { Redirect } from 'react-router';
const { version } = packageJSON;

// Routes whose entry starts the "load readiness checker" page-load SLI.
const LOAD_READINESS_SLI_PATHS = [Paths.JoinPage, Paths.ReadinessCheckPage];

const ReadinessCheckPage = lazy(() =>
  import('containers/pages/ReadinessCheck/ReadinessCheck')
);
const AgoraBroadcast = lazy(() =>
  import('./containers/pages/Broadcast/AgoraBroadcast')
);
const AgoraJoin = lazy(() =>
  import('./containers/pages/JoinV2/Agora/AgoraJoin')
);

const IntercomApp = lazy(() =>
  import('@studio/intercom/IntercomApp').then(module => ({
    default: module.default
  }))
);

const App = () => {
  const rudderstackDataPlaneUrl = getEnvVar(
    'REACT_APP_RUDDERSTACK_DATA_PLANE_URL'
  );
  const env = getEnvVar('REACT_APP_CVENT_ENV');
  const rudderstackWriteKey = getEnvVar('REACT_APP_RUDDERSTACK_WRITE_KEY');

  const [theme, setTheme] = useState(LIGHT_THEME);
  let user = useSelector(state => state.user, shallowEqual);
  const themeContext = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  const { brandingSettings } = useSelector(
    ({ brandingSettings }) => ({
      brandingSettings
    }),
    shallowEqual
  );

  const broadcastStartOn = useSelector(state => state.broadcast?.startOn);
  const broadcastEndOn = useSelector(state => state.broadcast?.endOn);
  const broadcastTitle = useSelector(state => state.broadcast?.title);
  const externalEventId = useSelector(state => state.event?.externalId);
  const eventTitle = useSelector(state => state.event?.title);
  const eventStartDate = useSelector(state => state.event?.startOn);
  const eventEndDate = useSelector(state => state.event?.endOn);
  const meetingStatus = useSelector(state => state.meeting.status);
  const accountMappingId = useSelector(state => state.team?.externalId);
  const broadcastExternalId = useSelector(state => state.broadcast?.externalId);
  let requireAuth = true;

  const isStandAloneReadinessCheckPage = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.location &&
      window.location.pathname === '/studio-presenter/check',
    []
  );

  const fakeUserForReadinessCheck = useMemo(
    () => ({
      firstName: 'Standalone',
      lastName: 'User',
      email: 'stand+alone+user@cvent.com',
      _id: `standalone-readiness-check-user-${nanoid()}`
    }),
    []
  );

  if (isStandAloneReadinessCheckPage) {
    // This is to allow access to the standalone readiness check page without authentication
    // so that it can be used as a standalone page for presenters to test their setup before joining a meeting
    // In this case, we mock a user object and set requireAuth to false, this bypasses the authentication check
    requireAuth = false;
    user = fakeUserForReadinessCheck;
  }

  // Only enforce authentication check when presenter auth is required
  useRequireAuth(requireAuth);

  const externalUserId = user?.externalId;
  const userId = user?._id;

  const query = new URLSearchParams(useLocation().search);
  const broadcastType = query.get(BROADCAST_TYPE);
  const appContextValue = {
    isStudioLite: Boolean(broadcastType),
    isPresenterStandalone: !Boolean(broadcastType)
  };

  useEffect(() => {
    if (externalUserId) {
      datadogRum.setGlobalContextProperty(
        'studio.external.user.id',
        externalUserId
      );
      datadogLogs.setGlobalContextProperty(
        'studio.external.user.id',
        externalUserId
      );
    }
  }, [externalUserId]);

  useEffect(() => {
    if (userId) {
      datadogRum.setGlobalContextProperty('studio.user.id', userId);
      datadogLogs.setGlobalContextProperty('studio.user.id', userId);

      datadogRum.setUser({
        id: userId
      });
    }
  }, [userId]);

  useEffect(() => {
    if (accountMappingId) {
      datadogRum.setGlobalContextProperty(
        'account_mapping.id',
        accountMappingId
      );
      datadogLogs.setGlobalContextProperty(
        'account_mapping.id',
        accountMappingId
      );
    }
  }, [accountMappingId]);

  const pathParams = getURLPathParams();

  const teamId = pathParams?.teamId;
  const eventId = pathParams?.eventId;
  const broadcastId = pathParams?.broadcastId;

  const joinRouteParams = query.size > 0 ? `?${query.toString()}` : '';
  const joinRoute = `/studio-presenter/teams/${teamId}/events/${eventId}/broadcasts/${broadcastId}/join${joinRouteParams}`;

  const intercomProps = {
    appId: getEnvVar('REACT_APP_INTERCOM_APP_ID'),
    intercomIdentityVerificationKey: getEnvVar(
      'REACT_APP_INTERCOM_IDENTITY_VERIFICATION_KEY'
    ),
    user: {
      ...(externalUserId && {
        id: userId,
        firstName: user.firstName,
        lastName: user.lastName,
        emailAddress: user.email,
        persona: ATTRIBUTE_PERSONA_TYPE_SPEAKER
      })
    },
    eventDetails: {
      eventId: pathParams?.eventId
    }
  };

  const ldKey = getEnvVar('REACT_APP_LAUNCHDARKLY_KEY');
  const ldPayload = useLDPayload();

  const isLDInitializedOrTimedOut = useLaunchDarkly(ldPayload, ldKey);

  if (!ldPayload || !isLDInitializedOrTimedOut) return <></>;

  const JoinPage = AgoraJoin;
  const BroadcastPage = AgoraBroadcast;
  const isUserInBroadcast = meetingStatus === MEETING_STATUS.STARTED;

  const metricsProps = {
    accountMappingId,
    attendeeId: externalUserId,
    studioUserId: userId,
    persona: ATTRIBUTE_PERSONA_TYPE_SPEAKER,
    meetingType: VENDOR_AGORA,
    rudderstackWriteKey,
    environmentName: getEnvironmentType(env) || 'test',
    rudderstackDataPlaneUrl,
    appVersion: version,
    broadcastId,
    teamId,
    studioEventId: eventId,
    sessionStartDate: new Date(broadcastStartOn),
    sessionEndDate: new Date(broadcastEndOn),
    broadcastType: broadcastTypesMap.get(broadcastType?.toLowerCase()),
    sessionName: broadcastTitle,
    sessionId: broadcastExternalId,
    eventId: externalEventId,
    eventName: eventTitle,
    eventStartDate: new Date(eventStartDate),
    eventEndDate: new Date(eventEndDate)
  };

  return (
    <MetricsProvider {...metricsProps}>
      <LoadReadinessCheckerSLIObserver paths={LOAD_READINESS_SLI_PATHS} />
      <AppContext.Provider value={appContextValue}>
        <BrandContext.Provider value={brandingSettings}>
          <ThemeContext.Provider value={themeContext}>
            <ThemeProvider theme={{ id: theme }}>
              {/* ThemeProvider - For styled components - provides theme via props.theme.id */}
              <Suspense fallback={<Loading />}>
                <Route exact path={Paths.BroadcastPage}>
                  {isUserInBroadcast ? (
                    <BroadcastPage />
                  ) : (
                    <Redirect to={joinRoute} />
                  )}
                </Route>
                <Route exact path={Paths.JoinPage} component={JoinPage} />
                <Route
                  exact
                  path={Paths.ReadinessCheckPage}
                  component={ReadinessCheckPage}
                />
              </Suspense>
              {!_.isEmpty(intercomProps.user) && (
                <Suspense fallback={<Loading />}>
                  <IntercomApp
                    appId={intercomProps.appId}
                    intercomIdentityVerificationKey={
                      intercomProps.intercomIdentityVerificationKey
                    }
                    userDetails={intercomProps.user}
                    eventDetails={intercomProps.eventDetails}
                  />
                </Suspense>
              )}
              <GlobalStyle />
              {/* modals */}
              <AwaitingKnockApprovalModal />
              <GenericSettingsModal />
              <ScreenshareModal />
              <InvalidJoinRequestModal />
              <MeetingEndedModal />
              <LostRealtimeSocketMessage />
              <CreateMeetingError />
              <UnauthorizedError />
              <AudioInputFailed />
            </ThemeProvider>
          </ThemeContext.Provider>
        </BrandContext.Provider>
      </AppContext.Provider>
    </MetricsProvider>
  );
};

export default App;
